import { useState, useCallback } from 'react';
import { apiFetch } from '../../../utils/apiClient';

const useTaskOperations = (
    token,
    leadId,
    timestamp,
    project,
    teamSwimlaneId,
    setTasks,
    fetchProjectData
) => {
    const [selectedTaskDetail, setSelectedTaskDetail] = useState(null);
    const [loadingTaskDetail, setLoadingTaskDetail] = useState(false);
    const [savingChanges, setSavingChanges] = useState(false);
    const [savingField, setSavingField] = useState(null); // 'status', 'assignment', 'description', etc.
    const [creatingTask, setCreatingTask] = useState(false);
    const [creatingSubtask, setCreatingSubtask] = useState(false);

    const fetchTaskDetail = useCallback(async (taskId, showLoading = true) => {
        if (showLoading) {
            setLoadingTaskDetail(true);
        }
        try {
            const response = await apiFetch(
                `${process.env.REACT_APP_API_URL}/client/task/${encodeURIComponent(taskId)}/details?leadId=${encodeURIComponent(leadId)}&timestamp=${encodeURIComponent(timestamp)}`
            );
            if (response.ok) {
                const taskData = await response.json();
                // Use functional update to prevent reopening closed modal
                // For background refreshes (showLoading = false), don't reopen if modal was closed
                setSelectedTaskDetail(prev => {
                    if (!showLoading && prev === null) {
                        return null; // Modal was closed, don't reopen
                    }
                    if (!showLoading && prev && String(prev.taskId) !== String(taskId)) {
                        return prev; // User navigated to a different task, don't override
                    }
                    return taskData;
                });
                return taskData;
            } else {
                throw new Error('Failed to load task details');
            }
        } catch (err) {
            console.error('❌ Task detail fetch error:', err);
            console.error('❌ Task ID:', taskId);
            console.error('❌ Response status:', err.status);
            console.error('❌ Full error:', JSON.stringify(err, null, 2));
            // If modal was closed (showLoading = false), don't throw error
            if (showLoading) {
                throw err;
            }
            return null;
        } finally {
            if (showLoading) {
                setLoadingTaskDetail(false);
            }
        }
    }, [token, leadId, timestamp]);

    const handleSaveChanges = useCallback(async (taskId, dataToSave, field = null) => {
        setSavingChanges(true);
        setSavingField(field);
        try {
            // Convert start field to custom_attributes_values for legacy compatibility
            let updates = { ...dataToSave };
            // Project window override travels as a top-level request-control flag,
            // never as task data (Bill, 2026-08-03)
            const allowBeforeKickoff = updates.allowBeforeKickoff;
            delete updates.allowBeforeKickoff;
            if ('start' in updates) {
                updates.custom_attributes_values = {
                    ...(updates.custom_attributes_values || {}),
                    'Start Date': updates.start
                };
                delete updates.start;
            }

            const response = await apiFetch(
                `${process.env.REACT_APP_API_URL}/client/task/${encodeURIComponent(taskId)}`,
                {
                    method: 'PUT',
                    headers: {
                        'Content-Type': 'application/json'
                    },
                    body: JSON.stringify({
                        updates: updates,
                        leadId: leadId,
                        timestamp: timestamp,
                        allowBeforeKickoff: allowBeforeKickoff
                    })
                }
            );
            if (response.ok) {
                // Refresh data - don't throw if this fails since the save succeeded
                try {
                    await fetchProjectData();
                } catch (refreshErr) {
                    console.error('Data refresh failed (save succeeded):', refreshErr);
                }
            } else {
                const errorData = await response.json();
                throw new Error(errorData.error || 'Failed to save changes');
            }
        } catch (err) {
            console.error('Save error:', err);
            throw err;
        } finally {
            setSavingChanges(false);
            setSavingField(null);
        }
    }, [leadId, timestamp, fetchProjectData]);

    const handleDeleteTask = useCallback(async (taskId) => {
        if (!window.confirm('Are you sure you want to delete this task? This cannot be undone.')) {
            return;
        }

        try {
            const response = await apiFetch(
                `${process.env.REACT_APP_API_URL}/client/task/${encodeURIComponent(taskId)}`,
                {
                    method: 'DELETE',
                    headers: {
                        'Content-Type': 'application/json'
                    },
                    body: JSON.stringify({
                        leadId: leadId,
                        timestamp: timestamp
                    })
                }
            );

            if (response.ok) {
                // If deleting the currently open task (handle both string and numeric IDs)
                if (selectedTaskDetail && String(selectedTaskDetail.taskId) === String(taskId)) {
                    // If this is a subtask, go back to parent task
                    if (selectedTaskDetail.mainTaskId) {
                        await fetchTaskDetail(selectedTaskDetail.mainTaskId, true);
                    } else {
                        // If main task, close the modal
                        setSelectedTaskDetail(null);
                    }
                }
                await fetchProjectData();
            } else {
                throw new Error('Failed to delete task');
            }
        } catch (err) {
            console.error('Delete task error:', err);
            throw err;
        }
    }, [leadId, timestamp, selectedTaskDetail, fetchTaskDetail, fetchProjectData]);

    const handleCreateTask = useCallback(async (taskData) => {
        setCreatingTask(true);
        try {
            const response = await apiFetch(
                `${process.env.REACT_APP_API_URL}/client/task`,
                {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json'
                    },
                    body: JSON.stringify({
                        subject: taskData.subject,
                        description: taskData.description,
                        projectId: project.projectId,
                        swimlaneId: teamSwimlaneId,
                        assignedUsers: taskData.assignedUsers || [],
                        assignedUsersNames: taskData.assignedUsersNames || [],
                        assignedToName: taskData.assignedToName,
                        leadId: leadId,
                        timestamp: timestamp
                    })
                }
            );

            if (response.ok) {
                await fetchProjectData();
            } else {
                const error = await response.json();
                throw new Error(error.error || 'Failed to create task');
            }
        } catch (err) {
            console.error('Create task error:', err);
            throw err;
        } finally {
            setCreatingTask(false);
        }
    }, [project, teamSwimlaneId, leadId, timestamp, fetchProjectData]);

    const handleCreateSubtask = useCallback(async (parentTaskId, subtaskData) => {
        setCreatingSubtask(true);
        try {
            const response = await apiFetch(
                `${process.env.REACT_APP_API_URL}/client/task`,
                {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json'
                    },
                    body: JSON.stringify({
                        subject: subtaskData.subject,
                        description: subtaskData.description,
                        projectId: project.projectId,
                        swimlaneId: teamSwimlaneId,
                        mainTaskId: parentTaskId,
                        assignedUsers: subtaskData.assignedUsers || [],
                        assignedToName: subtaskData.assignedToName,
                        start: subtaskData.start,
                        due_date: subtaskData.dueDate,
                        allowBeforeKickoff: subtaskData.allowBeforeKickoff,
                        leadId: leadId,
                        timestamp: timestamp
                    })
                }
            );

            if (response.ok) {
                await fetchTaskDetail(parentTaskId, false);
                await fetchProjectData();
            } else {
                throw new Error('Failed to create subtask');
            }
        } catch (err) {
            console.error('Subtask creation error:', err);
            throw err;
        } finally {
            setCreatingSubtask(false);
        }
    }, [project, teamSwimlaneId, leadId, timestamp, fetchTaskDetail, fetchProjectData]);

    const handleDrop = useCallback(async (draggedTask, statusId, columns, updateInProgressRef) => {
        if (!draggedTask) return;

        // Mark update in progress
        updateInProgressRef.current = true;

        // Find the status info
        const newStatusInfo = columns.find(col => col.id === statusId);

        // Optimistic update with status name (handle both string and numeric IDs)
        setTasks(prevTasks =>
        prevTasks.map(task =>
        String(task.taskId) === String(draggedTask.taskId)
        ? {
            ...task,
            status: statusId,
            statusName: newStatusInfo?.name || task.statusName
        }
        : task
        )
        );

        try {
            const response = await apiFetch(
                `${process.env.REACT_APP_API_URL}/client/task/${encodeURIComponent(draggedTask.taskId)}`,
                {
                    method: 'PUT',
                    headers: {
                        'Content-Type': 'application/json'
                    },
                    body: JSON.stringify({
                        status: statusId,
                        version: draggedTask.version,
                        leadId: leadId,
                        timestamp: timestamp
                    })
                }
            );

            if (!response.ok) {
                // Revert on failure (handle both string and numeric IDs)
                setTasks(prevTasks =>
                prevTasks.map(task =>
                String(task.taskId) === String(draggedTask.taskId) ? draggedTask : task
                )
                );
                throw new Error('Failed to update task');
            }

            // Mark update complete after a short delay
            setTimeout(() => {
                updateInProgressRef.current = false;
            }, 1000);

        } catch (err) {
            console.error('Update error:', err);
            // Revert on error (handle both string and numeric IDs)
            setTasks(prevTasks =>
            prevTasks.map(task =>
            String(task.taskId) === String(draggedTask.taskId) ? draggedTask : task
            )
            );
            updateInProgressRef.current = false;
            throw err;
        }
    }, [leadId, timestamp, setTasks]);

    return {
        selectedTaskDetail,
        setSelectedTaskDetail,
        loadingTaskDetail,
        savingChanges,
        savingField,
        creatingTask,
        creatingSubtask,
        fetchTaskDetail,
        handleSaveChanges,
        handleDeleteTask,
        handleCreateTask,
        handleCreateSubtask,
        handleDrop
    };
};

export default useTaskOperations;
